/**
 * Monitoring.js — Monitoramento em Tempo Real
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 * Data: 2026-06-11
 *
 * Funcionalidades:
 *   - Grid de câmeras com snapshot ao vivo (polling + WebSocket)
 *   - Exibição do frame real da última leitura de placa confirmada
 *   - Data e hora relativa na última detecção (Hoje, Ontem, ou data completa)
 *   - Alerta visual (toast) para acessos negados recebidos via Socket.io
 *   - Painel lateral de eventos recentes (acessos negados e detecções)
 *   - Indicador de câmera em estado de alerta quando acesso negado
 *   - Botão "Câmera ao Vivo" em cada card com modal de streaming MJPEG
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { SystemHealthPanel } from '../components/SystemHealthBadge';
import '../styles/Monitoring.css';

// Para imagens de snapshot, sempre usar URL relativa (via proxy Nginx) para evitar
// bloqueio de CORS/mixed-content quando frontend e backend estão em portas diferentes.
// O Nginx já tem proxy configurado para /api/ → backend:5000.
const SNAPSHOT_BASE = '';


// ─── Alerta Sonoro de Acesso Negado (Web Audio API) ─────────────────────────────────────────────

/**
 * Toca um bipe de alerta de acesso negado usando a Web Audio API.
 * Não requer nenhum arquivo de áudio externo.
 * Padrão: 2 bipes curtos (880Hz) + 1 bipe longo (440Hz).
 */
let _audioCtx = null;
function getAudioContext() {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return _audioCtx;
}

function playDeniedAlert() {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();
    const sequence = [
      { freq: 880, duration: 0.08, start: 0.0 },
      { freq: 880, duration: 0.08, start: 0.16 },
      { freq: 440, duration: 0.35, start: 0.36 },
    ];
    const now = ctx.currentTime;
    sequence.forEach(({ freq, duration, start }) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0.0, now + start);
      gain.gain.linearRampToValueAtTime(0.35, now + start + 0.005);
      gain.gain.setValueAtTime(0.35, now + start + duration - 0.02);
      gain.gain.linearRampToValueAtTime(0.0, now + start + duration);
      osc.start(now + start);
      osc.stop(now + start + duration);
    });
  } catch (err) {
    console.warn('[Alert Sound] Falha ao reproduzir alerta sonoro:', err.message);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DENIED_REASON_LABELS = {
  fora_horario_credenciado: 'Fora do horário (credenciado)',
  fora_horario_mensalista:  'Fora do horário (mensalista)',
  morador_inativo:          'Morador inativo',
  veiculo_bloqueado:        'Veículo bloqueado',
  visitante_expirado:       'Autorização expirada',
  nao_cadastrado:           'Não cadastrado',
  erro_lookup:              'Erro de consulta',
};

function getDeniedLabel(reason) {
  return DENIED_REASON_LABELS[reason] || reason || 'Acesso negado';
}

/**
 * Formata uma data/hora com referência relativa ao dia atual.
 * - Hoje:   "Hoje às 06:46:08"
 * - Ontem:  "Ontem às 23:15:42"
 * - Outros: "10/06/2026 às 06:46:08"
 */
function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const timeStr  = date.toLocaleTimeString('pt-BR');
  if (dateOnly.getTime() === today.getTime()) {
    return `Hoje às ${timeStr}`;
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    return `Ontem às ${timeStr}`;
  } else {
    return `${date.toLocaleDateString('pt-BR')} às ${timeStr}`;
  }
}

function formatConfidence(conf) {
  if (conf === null || conf === undefined) return '—';
  return `${(parseFloat(conf) * 100).toFixed(1)}%`;
}

// ─── Modal de Streaming MJPEG ─────────────────────────────────────────────────

/**
 * LiveStreamModal
 * Abre um modal com o stream MJPEG ao vivo da câmera selecionada.
 * O token JWT é passado via query string para autenticação do endpoint de stream.
 *
 * Props:
 *   camera   — objeto da câmera { id, name, camera_type }
 *   onClose  — callback para fechar o modal
 */
const LiveStreamModal = ({ camera, onClose }) => {
  const [streamError, setStreamError]   = useState(false);
  const [streamLoading, setStreamLoading] = useState(true);
  const [elapsed, setElapsed]           = useState(0);
  const imgRef    = useRef(null);
  const timerRef  = useRef(null);
  const startedAt = useRef(Date.now());

  // Obter token JWT do localStorage (mesmo mecanismo do api.js)
  const token = localStorage.getItem('token') || '';

  // URL do stream MJPEG — token via query string (tag <img> não suporta headers)
  const streamUrl = `${SNAPSHOT_BASE}/api/stream/${camera.id}?token=${encodeURIComponent(token)}&t=${Date.now()}`;

  // Contador de tempo ao vivo
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Fechar com Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Formatar tempo decorrido como mm:ss
  const formatElapsed = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="live-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="live-modal">
        {/* Header do modal */}
        <div className="live-modal-header">
          <div className="live-modal-title">
            <span className="live-modal-dot" />
            <span>AO VIVO</span>
            <span className="live-modal-cam-name">{camera.name}</span>
            <span className="live-modal-elapsed">{formatElapsed(elapsed)}</span>
          </div>
          <button className="live-modal-close" onClick={onClose} title="Fechar (Esc)">✕</button>
        </div>

        {/* Área do stream */}
        <div className="live-modal-body">
          {streamLoading && !streamError && (
            <div className="live-modal-loading">
              <div className="live-modal-spinner" />
              <p>Abrindo a imagem da câmera...</p>
              <p className="live-modal-hint">Leva um ou dois segundos.</p>
            </div>
          )}

          {streamError && (
            <div className="live-modal-error">
              <span className="live-modal-error-icon">📡</span>
              <p>Não há imagem desta câmera agora.</p>
              <p className="live-modal-hint">
                A imagem vem da captura que a plataforma já faz: se a câmera estiver sem sinal, a faixa
                vermelha "Câmera sem imagem" aparece no topo da tela.
              </p>
              <button
                className="live-modal-retry"
                onClick={() => {
                  setStreamError(false);
                  setStreamLoading(true);
                  // Forçar recarregamento da tag img com novo timestamp
                  if (imgRef.current) {
                    imgRef.current.src = `${SNAPSHOT_BASE}/api/stream/${camera.id}?token=${encodeURIComponent(token)}&t=${Date.now()}`;
                  }
                }}
              >
                Tentar novamente
              </button>
            </div>
          )}

          {/* Tag <img> que recebe o stream MJPEG nativamente */}
          <img
            ref={imgRef}
            src={streamUrl}
            alt={`Stream ao vivo — ${camera.name}`}
            className={`live-modal-stream ${streamLoading || streamError ? 'live-modal-stream-hidden' : ''}`}
            onLoad={() => { setStreamLoading(false); setStreamError(false); }}
            onError={() => { setStreamLoading(false); setStreamError(true); }}
          />
        </div>

        {/* Rodapé informativo */}
        <div className="live-modal-footer">
          <span>📷 1 quadro por segundo, da captura em andamento</span>
          <span>Olhar não abre conexão nova com a câmera</span>
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const Monitoring = () => {
  const { socket, can } = useAuth();
  // Onda 149: o "Ao Vivo" exige `cameras_live` — o perfil Supervisão vê o Monitoramento sem ele.
  const podeAoVivo = typeof can === 'function' ? can('cameras_live') : true;
  const [cameras, setCameras]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [lastUpdate, setLastUpdate]       = useState(null);

  // ── Alertas de acesso negado ──────────────────────────────────────────────
  const [deniedAlerts, setDeniedAlerts]   = useState([]); // toasts
  const [recentEvents, setRecentEvents]   = useState([]); // painel lateral
  const [cameraAlerts, setCameraAlerts]   = useState({}); // { cameraId: true }
  // condo_alert_sound: carregado do backend; true por padrão para não silenciar sem configuração
  const [alertSoundEnabled, setAlertSoundEnabled] = useState(true);
  // audioUnlocked: true após o primeiro clique do usuário (política de autoplay dos navegadores)
  const audioUnlocked = useRef(false);
  const alertTimers = useRef({});

  // ── Modal de streaming ao vivo ────────────────────────────────────────────
  // liveCamera: objeto da câmera sendo exibida no modal, ou null se fechado
  const [liveCamera, setLiveCamera] = useState(null);

  // ── Painel de Saúde — preferência persistida no localStorage ─────────────
  const [showHealthPanel, setShowHealthPanel] = useState(
    () => localStorage.getItem('monitoring_health_panel') !== 'false'
  );

  function toggleHealthPanel() {
    setShowHealthPanel(prev => {
      const next = !prev;
      localStorage.setItem('monitoring_health_panel', String(next));
      return next;
    });
  }

  // ─── Desbloquear áudio após primeiro clique (política de autoplay) ──────────────────────────────
  useEffect(() => {
    const unlock = () => {
      if (!audioUnlocked.current) {
        audioUnlocked.current = true;
        try {
          const ctx = getAudioContext();
          if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        } catch (_) {}
      }
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // ─── Carregar configuração de som de alerta do backend ──────────────────────────────────────────
  useEffect(() => {
    api.get('/api/settings')
      .then(res => {
        const val = res.data?.condo_alert_sound;
        if (val !== undefined && val !== null) {
          setAlertSoundEnabled(String(val) === 'true');
        }
      })
      .catch(() => {}); // Falha silenciosa — mantém o padrão (true)
  }, []);

  // ─── Carregar câmeras ─────────────────────────────────────────────────────

  const fetchCameras = useCallback(async () => {
    try {
      const response = await api.get('/api/monitoring/cameras');
      setCameras(response.data);
      setLastUpdate(new Date());
      // Para câmeras que já têm plate_snapshot no banco, inicializar o ts
      // diretamente no objeto da câmera (se ainda não foi definido via WebSocket)
      setCameras(prev => {
        const prevMap = {};
        prev.forEach(c => { prevMap[c.id] = c; });
        return response.data.map(cam => ({
          ...cam,
          plate_snapshot_ts: (prevMap[cam.id] && prevMap[cam.id].plate_snapshot_ts)
            ? prevMap[cam.id].plate_snapshot_ts
            : (cam.has_plate_snapshot ? Date.now() : null)
        }));
      });
    } catch (error) {
      console.error('Erro ao carregar câmeras:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCameras();
    const interval = setInterval(fetchCameras, 5000);
    return () => clearInterval(interval);
  }, [fetchCameras]);

  // ─── WebSocket: monitoramento de frames ──────────────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleMonitoringUpdate = (data) => {
      const { cameraId, plate, confidence, timestamp } = data;

      // Atualização unificada em um único setCameras:
      // texto da placa + plate_snapshot_ts mudam no mesmo render → zero delay entre imagem e texto
      const nowTs = Date.now();
      setCameras(prev => prev.map(cam => {
        if (cam.id !== cameraId) return cam;
        return {
          ...cam,
          last_heartbeat: timestamp,
          status: 'ativa',
          last_error: null,
          last_plate: plate ? { plate, confidence, detected_at: timestamp } : cam.last_plate,
          has_plate_snapshot: plate ? true : cam.has_plate_snapshot,
          // plate_snapshot_ts atualizado junto com last_plate — mesmo render, zero delay
          plate_snapshot_ts: plate ? nowTs : cam.plate_snapshot_ts
        };
      }));

      setLastUpdate(new Date());
    };

    socket.on('monitoring_update', handleMonitoringUpdate);
    return () => socket.off('monitoring_update', handleMonitoringUpdate);
  }, [socket]);

  // ─── WebSocket: alertas de acesso negado ─────────────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleAccessDenied = (data) => {
      const { plate, reason, camera_id, timestamp } = data;
      const id = Date.now();
      const event = {
        id,
        plate:     plate || '—',
        reason:    getDeniedLabel(reason),
        camera_id: camera_id || null,
        time:      new Date(timestamp || Date.now())
      };

      // Adicionar toast (auto-remove após 8s)
      setDeniedAlerts(prev => [event, ...prev].slice(0, 5));
      setTimeout(() => {
        setDeniedAlerts(prev => prev.filter(a => a.id !== id));
      }, 8000);

      // Adicionar ao painel lateral de eventos recentes
      setRecentEvents(prev => [event, ...prev].slice(0, 50));

      // Marcar câmera com alerta visual (pisca por 10s)
      if (camera_id) {
        setCameraAlerts(prev => ({ ...prev, [camera_id]: true }));
        if (alertTimers.current[camera_id]) clearTimeout(alertTimers.current[camera_id]);
        alertTimers.current[camera_id] = setTimeout(() => {
          setCameraAlerts(prev => { const n = { ...prev }; delete n[camera_id]; return n; });
        }, 10000);
      }

      // Alerta sonoro (apenas se habilitado nas configurações e áudio desbloqueado pelo usuário)
      if (alertSoundEnabled && audioUnlocked.current) {
        playDeniedAlert();
      }
    };

    socket.on('access_denied', handleAccessDenied);
    return () => socket.off('access_denied', handleAccessDenied);
  }, [socket, alertSoundEnabled]);

  // ─── Helpers de UI ───────────────────────────────────────────────────────

  const getStatusBadge = (status) => {
    const map = {
      ativa:   { label: 'Ativa',   cls: 'badge-active' },
      erro:    { label: 'Erro',    cls: 'badge-error' },
      inativa: { label: 'Inativa', cls: 'badge-inactive' }
    };
    return map[status] || { label: status, cls: 'badge-inactive' };
  };

  const getCameraTypeLabel = (type) =>
    type === 'entrada' ? '⬆ Entrada' : '⬇ Saída';

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="monitoring">
        <div className="monitoring-loading">
          <div className="spinner" />
          <p>Carregando câmeras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="monitoring">

      {/* ── Modal de Streaming ao Vivo ──────────────────────────────────── */}
      {liveCamera && (
        <LiveStreamModal
          camera={liveCamera}
          onClose={() => setLiveCamera(null)}
        />
      )}

      {/* ── Toasts de Acesso Negado ─────────────────────────────────────── */}
      <div className="denied-toasts-container">
        {deniedAlerts.map(alert => (
          <div key={alert.id} className="denied-toast">
            <div className="denied-toast-icon">🚫</div>
            <div className="denied-toast-content">
              <div className="denied-toast-title">Acesso Negado</div>
              <div className="denied-toast-plate">{alert.plate}</div>
              <div className="denied-toast-reason">{alert.reason}</div>
            </div>
            <button
              className="denied-toast-close"
              onClick={() => setDeniedAlerts(prev => prev.filter(a => a.id !== alert.id))}
            >✕</button>
          </div>
        ))}
      </div>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="monitoring-header">
        <div>
          <h1>Monitoramento em Tempo Real</h1>
          <p className="monitoring-subtitle">
            Última atualização: {lastUpdate ? lastUpdate.toLocaleTimeString('pt-BR') : '—'}
          </p>
        </div>
        <div className="monitoring-legend">
          <span className="legend-item"><span className="dot dot-active" /> Ativa</span>
          <span className="legend-item"><span className="dot dot-error" /> Erro</span>
          <span className="legend-item"><span className="dot dot-denied" /> Acesso Negado</span>
          <label className="health-toggle-label" title={showHealthPanel ? 'Ocultar painel de saúde dos serviços' : 'Exibir painel de saúde dos serviços'}>
            <span className="health-toggle-text">Saúde do Sistema</span>
            <span
              className={`health-toggle-switch ${showHealthPanel ? 'health-toggle-on' : 'health-toggle-off'}`}
              onClick={toggleHealthPanel}
              role="switch"
              aria-checked={showHealthPanel}
              tabIndex={0}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleHealthPanel()}
            >
              <span className="health-toggle-thumb" />
            </span>
          </label>
        </div>
      </div>

      {/* ── Painel de Saúde dos Serviços ────────────────────────────────── */}
      {showHealthPanel && <SystemHealthPanel intervalMs={30000} />}

      <div className="monitoring-layout">

        {/* ── Grid de Câmeras ─────────────────────────────────────────── */}
        <div className="monitoring-main">
          {cameras.length === 0 ? (
            <div className="monitoring-empty">
              <p>Nenhuma câmera cadastrada. Acesse <strong>Câmeras</strong> para adicionar.</p>
            </div>
          ) : (
            <div className="camera-grid">
              {cameras.filter(cam => cam.status !== 'inativa').map(cam => {
                const badge    = getStatusBadge(cam.status);
                const hasPlate = cam.last_plate && cam.last_plate.plate;
                const isDenied = !!cameraAlerts[cam.id];

                // URL do frame da última leitura confirmada de placa.
                // Só é exibida quando existe um plate_snapshot disponível.
                // O timestamp garante que o browser não use cache ao recarregar.
                // plate_snapshot_ts está no próprio objeto cam — mesmo render que last_plate
                const plateTs          = cam.plate_snapshot_ts || null;
                const plateSnapshotUrl = plateTs
                  ? `${SNAPSHOT_BASE}/api/monitoring/plate-snapshot/${cam.id}?token=${encodeURIComponent(localStorage.getItem('token') || '')}&t=${plateTs}`
                  : null;

                return (
                  <div
                    key={cam.id}
                    className={`camera-card ${cam.status === 'erro' ? 'card-error' : ''} ${isDenied ? 'card-denied' : ''}`}
                  >
                    {/* Cabeçalho */}
                    <div className="camera-card-header">
                      <div className="camera-title">
                        <span className={`dot ${isDenied ? 'dot-denied' : badge.cls}`} />
                        <h2>{cam.name}</h2>
                      </div>
                      <div className="camera-meta">
                        <span className="camera-type">{getCameraTypeLabel(cam.camera_type)}</span>
                        {isDenied
                          ? <span className="status-badge badge-denied">⚠ Acesso Negado</span>
                          : <span className={`status-badge ${badge.cls}`}>{badge.label}</span>
                        }
                      </div>
                    </div>

                    {/* Frame da última leitura confirmada de placa */}
                    <div className={`camera-snapshot-wrapper ${isDenied ? 'snapshot-denied' : ''}`}>
                      {plateSnapshotUrl ? (
                        <img
                          key={plateTs}
                          src={plateSnapshotUrl}
                          alt={`Última leitura — ${cam.name}`}
                          className="camera-snapshot"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' width='640' height='360'><rect width='100%25' height='100%25' fill='%231a1a2e'/><text x='50%25' y='44%25' font-family='Arial' font-size='32' fill='%23444' text-anchor='middle'>📷</text><text x='50%25' y='60%25' font-family='Arial' font-size='14' fill='%23555' text-anchor='middle'>Aguardando primeira captura...</text></svg>`;
                          }}
                        />
                      ) : (
                        /* Placeholder enquanto nenhuma placa foi lida ainda */
                        <div className="camera-snapshot snapshot-placeholder">
                          <span className="placeholder-icon">📷</span>
                          <span className="placeholder-text">Aguardando primeira captura...</span>
                        </div>
                      )}

                      {isDenied && (
                        <div className="denied-overlay">
                          <span className="denied-overlay-icon">🚫</span>
                          <span className="denied-overlay-text">ACESSO NEGADO</span>
                        </div>
                      )}

                      {/* Badge "ÚLTIMA LEITURA" — indica que a imagem é do frame da placa */}
                      {plateSnapshotUrl && !isDenied && (
                        <span className="plate-capture-badge">
                          📸 Última Leitura
                        </span>
                      )}
                    </div>

                    {/* Resultado do OCR */}
                    <div className={`plate-result ${hasPlate ? 'plate-detected' : 'plate-none'} ${isDenied ? 'plate-denied' : ''}`}>
                      {hasPlate ? (
                        <>
                          <div className="plate-number">{cam.last_plate.plate}</div>
                          <div className="plate-details">
                            <span>Confiança: <strong>{formatConfidence(cam.last_plate.confidence)}</strong></span>
                            <span>Detectada: <strong>{formatDateTime(cam.last_plate.detected_at)}</strong></span>
                          </div>
                        </>
                      ) : (
                        <div className="plate-none-msg">
                          <span className="plate-none-icon">🔍</span>
                          <span>Nenhuma Placa Detectada</span>
                        </div>
                      )}
                    </div>

                    {/* Rodapé com botão Câmera ao Vivo */}
                    <div className="camera-card-footer">
                      <span>Heartbeat: {formatDateTime(cam.last_heartbeat)}</span>
                      <div className="camera-footer-right">
                        {cam.last_error && (
                          <span className="error-msg" title={cam.last_error}>
                            ⚠ {cam.last_error.substring(0, 30)}{cam.last_error.length > 30 ? '…' : ''}
                          </span>
                        )}
                        {/* Botão Câmera ao Vivo — só exibido para câmeras ativas */}
                        {cam.status === 'ativa' && podeAoVivo && (
                          <button
                            className="btn-live-stream"
                            onClick={() => setLiveCamera(cam)}
                            title="Abrir streaming ao vivo desta câmera"
                          >
                            <span className="btn-live-dot" />
                            Ao Vivo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Painel de Eventos Recentes ──────────────────────────────── */}
        {recentEvents.length > 0 && (
          <div className="monitoring-events-panel">
            <div className="events-panel-header">
              <h3>⚠ Acessos Negados</h3>
              <button
                className="events-panel-clear"
                onClick={() => setRecentEvents([])}
              >Limpar</button>
            </div>
            <div className="events-panel-list">
              {recentEvents.map(ev => (
                <div key={ev.id} className="event-item denied">
                  <div className="event-plate">{ev.plate}</div>
                  <div className="event-reason">{ev.reason}</div>
                  <div className="event-time">{formatDateTime(ev.time)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Monitoring;
